import { readSession } from "@/lib/auth";

import { type AppPalette, normalizeHex, validatePalette } from "./palette";

const STORAGE_KEY = "scola.themePalette";

type PaletteStore = Record<string, AppPalette>;

function isPalette(value: unknown): value is AppPalette {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  const keys: Array<keyof AppPalette> = [
    "page",
    "surface",
    "sidebar",
    "sidebarText",
    "sidebarActive",
    "accent",
    "header",
  ];
  return keys.every(
    (key) => typeof record[key] === "string" && normalizeHex(record[key]) === record[key]
  );
}

function readStore(): PaletteStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const store: PaletteStore = {};
    for (const [tenantId, value] of Object.entries(parsed)) {
      if (!tenantId || !isPalette(value) || validatePalette(value)) continue;
      store[tenantId] = value;
    }
    return store;
  } catch {
    return {};
  }
}

function writeStore(store: PaletteStore): void {
  try {
    const keys = Object.keys(store);
    if (keys.length === 0) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // La palette reste appliquée pour cette session même si le navigateur refuse l'écriture.
  }
}

export function sessionTenantId(): string | null {
  const user = readSession()?.user;
  const tenantId = user?.tenant_id ?? user?.app_metadata?.tenant_id ?? null;
  return tenantId && tenantId.length > 0 ? tenantId : null;
}

export function readTenantPalette(tenantId: string | null | undefined): AppPalette | null {
  if (!tenantId) return null;
  return readStore()[tenantId] ?? null;
}

export function writeTenantPalette(tenantId: string, palette: AppPalette): void {
  const store = readStore();
  store[tenantId] = palette;
  writeStore(store);
}

export function removeTenantPalette(tenantId: string): void {
  const store = readStore();
  if (!(tenantId in store)) return;
  delete store[tenantId];
  writeStore(store);
}
